import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon, Send, Wand2, BarChart3, Code, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SimpleEditor from '../components/SimpleEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const ArticlePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPublish, setShowPublish] = useState(false);
  const [showImageGen, setShowImageGen] = useState(false);
  const [selectedSite, setSelectedSite] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generatingInfographic, setGeneratingInfographic] = useState(false);
  const [editMode, setEditMode] = useState('visual'); // 'visual' ou 'html'

  const loadArticle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
      setImagePrompt(`Create a compelling featured image for an article titled: ${response.data.title}`);
    } catch (error) {
      toast.error('Article non trouvé');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wordpress-sites`);
      setSites(response.data);
    } catch (error) {
      console.error('Error loading sites');
    }
  }, []);

  useEffect(() => {
    loadArticle();
    loadSites();
  }, [loadArticle, loadSites]);

  const handleGenerateImage = async () => {
    if (!imagePrompt) {
      toast.error('Veuillez entrer une description');
      return;
    }

    setGeneratingImage(true);
    try {
      const response = await axios.post(`${API}/images/generate`, {
        prompt: imagePrompt,
        article_id: id
      });

      toast.success('Image générée avec succès');
      setArticle({ ...article, featured_image_url: response.data.image_url });
      setShowImageGen(false);
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handlePublish = async (forcePublish = false) => {
    if (!selectedSite) {
      toast.error('Veuillez sélectionner un site');
      return;
    }

    setPublishing(true);
    try {
      // Sauvegarder l'article avant de publier (important pour les modifications WYSIWYG)
      await axios.put(`${API}/articles/${id}`, article);
      
      const response = await axios.post(`${API}/wordpress-sites/${selectedSite}/publish`, {
        article_id: id,
        categories: article.categories || [],
        tags: article.keywords || [],
        publish_status: 'publish',
        force_publish: forcePublish
      });

      if (response.data?.warning) {
        const score = response.data.seo_score;
        setPublishing(false);
        
        toast.custom(
          (toastId) => (
            <div className="bg-slate-900 border border-amber-500/50 rounded-lg p-4 shadow-lg max-w-sm">
              <p className="font-semibold text-amber-400">Score SEO faible ({score}/100)</p>
              <p className="text-sm mt-1 text-slate-300">Voulez-vous publier quand même ?</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { toast.dismiss(toastId); handlePublish(true); }}
                  className="px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-sm font-semibold">
                  Forcer
                </button>
                <button onClick={() => toast.dismiss(toastId)}
                  className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm">
                  Annuler
                </button>
              </div>
            </div>
          ),
          { duration: 30000 }
        );
        return;
      }

      toast.success('Article publié avec succès!');
      setShowPublish(false);
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setPublishing(false);
    }
  };

  const handleUpdate = async (field, value) => {
    try {
      const updatedArticle = { ...article, [field]: value };
      await axios.put(`${API}/articles/${id}`, updatedArticle);
      setArticle(updatedArticle);
      toast.success('Mis à jour');
    } catch (error) {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleGenerateInfographic = async () => {
    if (!article.key_figure) {
      toast.error('Aucun chiffre cle dans cet article');
      return;
    }
    setGeneratingInfographic(true);
    try {
      const response = await axios.post(`${API}/articles/${id}/generate-infographic`);
      setArticle({ ...article, infographic_url: response.data.image_url });
      toast.success(`Infographie generee (${response.data.cost_eur?.toFixed(3)} EUR)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur generation infographie');
    } finally {
      setGeneratingInfographic(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8 flex items-center justify-center">
        <p className="text-slate-400 text-lg">Chargement...</p>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen p-8" data-testid="article-preview-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            data-testid="btn-back"
            className="flex items-center text-sky-400 hover:text-sky-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour
          </button>

          <div className="flex gap-4">
            {article.key_figure && (
              <Button
                onClick={handleGenerateInfographic}
                disabled={generatingInfographic}
                data-testid="btn-generate-infographic"
                className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border-2 border-amber-500/50 hover:border-amber-400 uppercase tracking-widest font-bold px-6 py-3 transition-all duration-300"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {generatingInfographic ? 'Generation...' : 'Infographie'}
              </Button>
            )}
            <Button
              onClick={() => setShowImageGen(true)}
              data-testid="btn-generate-image"
              className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border-2 border-purple-500/50 hover:border-purple-400 uppercase tracking-widest font-bold px-6 py-3 transition-all duration-300"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Générer image
            </Button>
            <Button
              onClick={() => setShowPublish(true)}
              data-testid="btn-publish"
              className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold px-6 py-3 transition-all duration-300 glow-sky hover:glow-sky-strong"
            >
              <Send className="w-4 h-4 mr-2" />
              Publier
            </Button>
          </div>
        </div>

        <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60 hud-marker" data-testid="article-content">
          <CardContent className="p-8">
            {/* Title Selection - Choix entre titre utilisateur et titre LLM */}
            {(article.title_user || article.title_llm) && article.title_user !== article.title_llm && (
              <div className="mb-6 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <label className="text-sm text-slate-400 uppercase tracking-wider mb-3 block">Choisir le titre</label>
                <div className="space-y-2">
                  {article.title_user && (
                    <label className="flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-colors border border-transparent hover:border-sky-500/30">
                      <input
                        type="radio"
                        name="title_choice"
                        checked={article.title === article.title_user}
                        onChange={() => setArticle({ ...article, title: article.title_user, title_source: 'user' })}
                        className="mt-1 accent-sky-500"
                        data-testid="radio-title-user"
                      />
                      <div className="flex-1">
                        <span className="text-xs text-sky-400 uppercase tracking-wider font-bold">Votre titre</span>
                        <p className="text-slate-200 mt-1">{article.title_user}</p>
                      </div>
                    </label>
                  )}
                  {article.title_llm && (
                    <label className="flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-colors border border-transparent hover:border-purple-500/30">
                      <input
                        type="radio"
                        name="title_choice"
                        checked={article.title === article.title_llm}
                        onChange={() => setArticle({ ...article, title: article.title_llm, title_source: 'llm' })}
                        className="mt-1 accent-purple-500"
                        data-testid="radio-title-llm"
                      />
                      <div className="flex-1">
                        <span className="text-xs text-purple-400 uppercase tracking-wider font-bold">Titre suggéré par l'IA</span>
                        <p className="text-slate-200 mt-1">{article.title_llm}</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* SEO Slug - Editable */}
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <span>Slug URL</span>
                  {article.custom_slug && (
                    <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">PERSONNALISÉ</span>
                  )}
                </label>
                {article.custom_slug && (
                  <button
                    type="button"
                    onClick={() => {
                      setArticle({ ...article, custom_slug: null });
                      handleUpdate('custom_slug', null);
                    }}
                    className="text-[10px] text-slate-500 hover:text-amber-400 transition-colors"
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm">/</span>
                <Input
                  data-testid="input-seo-slug"
                  value={article.custom_slug || article.seo_slug || article.desired_slug || ''}
                  onChange={(e) => {
                    // Nettoyer le slug: minuscules, tirets, pas d'espaces
                    const cleanSlug = e.target.value
                      .toLowerCase()
                      .replace(/\s+/g, '-')
                      .replace(/[^a-z0-9-]/g, '')
                      .replace(/-+/g, '-');
                    setArticle({ ...article, custom_slug: cleanSlug });
                  }}
                  onBlur={(e) => {
                    const cleanSlug = e.target.value
                      .toLowerCase()
                      .replace(/\s+/g, '-')
                      .replace(/[^a-z0-9-]/g, '')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                    if (cleanSlug !== (article.seo_slug || article.desired_slug)) {
                      handleUpdate('custom_slug', cleanSlug);
                    }
                  }}
                  placeholder="slug-de-l-article"
                  className="flex-1 bg-slate-950/50 border-slate-700 text-emerald-400 font-mono text-sm"
                />
              </div>
              <p className="text-[10px] text-slate-600 mt-1">
                Ce slug sera utilisé comme URL sur WordPress. Laissez vide pour utiliser le slug auto-généré.
              </p>
            </div>

            {/* Title - Editable */}
            <Input
              data-testid="input-article-title"
              value={article.title}
              onChange={(e) => setArticle({ ...article, title: e.target.value })}
              onBlur={(e) => handleUpdate('title', e.target.value)}
              className="text-3xl font-bold font-orbitron bg-transparent border-0 border-b border-slate-800 rounded-none px-0 text-sky-300 focus:border-sky-500 mb-6"
            />

            {/* Featured Image */}
            {article.featured_image_url && (
              <div className="mb-6">
                <img
                  src={article.featured_image_url}
                  alt="Featured"
                  className="w-full h-64 object-cover rounded border-2 border-slate-800"
                  data-testid="featured-image"
                />
              </div>
            )}

            {/* Summary */}
            <div className="mb-6">
              <label className="text-sm text-slate-400 uppercase tracking-wider">Résumé</label>
              <Textarea
                data-testid="textarea-summary"
                value={article.summary || ''}
                onChange={(e) => setArticle({ ...article, summary: e.target.value })}
                onBlur={(e) => handleUpdate('summary', e.target.value)}
                className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[80px]"
              />
            </div>

            {/* Content */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider">Contenu</label>
                <button
                  type="button"
                  onClick={() => setEditMode(editMode === 'visual' ? 'html' : 'visual')}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-sky-400 transition-colors"
                  title={editMode === 'html' ? 'Mode visuel' : 'Mode HTML'}
                >
                  {editMode === 'html' ? (
                    <><Eye className="w-3 h-3" /> Visuel</>
                  ) : (
                    <><Code className="w-3 h-3" /> HTML</>
                  )}
                </button>
              </div>
              {editMode === 'html' ? (
                <Textarea
                  data-testid="textarea-content"
                  value={article.content}
                  onChange={(e) => setArticle({ ...article, content: e.target.value })}
                  onBlur={(e) => handleUpdate('content', e.target.value)}
                  className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[400px] font-mono text-sm"
                />
              ) : (
                <SimpleEditor
                  value={article.content}
                  onChange={(value) => {
                    setArticle({ ...article, content: value });
                  }}
                  placeholder="Contenu de l'article..."
                />
              )}
              {editMode === 'visual' && (
                <p className="text-xs text-slate-600 mt-2">
                  Les modifications sont sauvegardées quand vous quittez la page ou publiez l'article.
                </p>
              )}
            </div>

            {/* Keywords */}
            <div className="mb-6">
              <label className="text-sm text-slate-400 uppercase tracking-wider">Mots-clés</label>
              <Input
                data-testid="input-keywords"
                value={article.keywords?.join(', ') || ''}
                onChange={(e) => setArticle({ ...article, keywords: e.target.value.split(',').map(k => k.trim()) })}
                onBlur={(e) => handleUpdate('keywords', e.target.value.split(',').map(k => k.trim()))}
                placeholder="mot1, mot2, mot3"
                className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
              />
            </div>

            {/* Key Figure & Infographic */}
            {(article.key_figure || article.infographic_url) && (
              <div className="mb-6 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                <label className="text-sm text-amber-400 uppercase tracking-wider font-bold">Chiffre cle (ACF)</label>
                <div className="mt-2 flex items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3">
                      <span className="text-3xl font-bold text-amber-300" style={{ fontFamily: "'Orbitron', monospace" }} data-testid="key-figure-value">
                        {article.key_figure || '-'}
                      </span>
                    </div>
                    <p className="text-slate-400 mt-1" data-testid="key-figure-legend">
                      {article.key_figure_legend || ''}
                    </p>
                    {article.focus_keyword && (
                      <p className="text-xs text-sky-500 mt-2">
                        Focus keyword : <strong>{article.focus_keyword}</strong>
                      </p>
                    )}
                  </div>
                  {article.infographic_url && (
                    <div className="flex-shrink-0">
                      <img
                        src={article.infographic_url}
                        alt="Infographie"
                        className="w-48 h-32 object-cover rounded border border-amber-500/30"
                        data-testid="infographic-image"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SEO */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 uppercase tracking-wider">Titre SEO</label>
                <Input
                  data-testid="input-seo-title"
                  value={article.seo_title || ''}
                  onChange={(e) => setArticle({ ...article, seo_title: e.target.value })}
                  onBlur={(e) => handleUpdate('seo_title', e.target.value)}
                  className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 uppercase tracking-wider">Description SEO</label>
                <Input
                  data-testid="input-seo-description"
                  value={article.seo_description || ''}
                  onChange={(e) => setArticle({ ...article, seo_description: e.target.value })}
                  onBlur={(e) => handleUpdate('seo_description', e.target.value)}
                  className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Image Generation Dialog */}
        <Dialog open={showImageGen} onOpenChange={setShowImageGen}>
          <DialogContent className="bg-slate-950 border-sky-500/50">
            <DialogHeader>
              <DialogTitle className="text-2xl font-orbitron text-sky-400">Générer une image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-slate-400 uppercase tracking-wider">Description de l'image</label>
                <Textarea
                  data-testid="textarea-image-prompt"
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder="Décrivez l'image que vous souhaitez générer..."
                  className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[120px]"
                />
              </div>
              <Button
                onClick={handleGenerateImage}
                disabled={generatingImage}
                data-testid="btn-submit-image-gen"
                className="w-full bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold py-6"
              >
                <Wand2 className="w-5 h-5 mr-2" />
                {generatingImage ? 'Génération...' : 'Générer'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Publish Dialog */}
        <Dialog open={showPublish} onOpenChange={setShowPublish}>
          <DialogContent className="bg-slate-950 border-sky-500/50">
            <DialogHeader>
              <DialogTitle className="text-2xl font-orbitron text-sky-400">Publier sur WordPress</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {sites.length === 0 ? (
                <p className="text-slate-400 text-center py-4">Aucun site configuré</p>
              ) : (
                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Sélectionner un site</label>
                  <div className="space-y-2 mt-2">
                    {sites.map((site) => (
                      <div
                        key={site.id}
                        onClick={() => setSelectedSite(site.id)}
                        data-testid={`site-option-${site.id}`}
                        className={`p-4 border rounded cursor-pointer transition-all ${
                          selectedSite === site.id
                            ? 'bg-sky-500/10 border-sky-500/50'
                            : 'bg-slate-900/50 border-slate-800 hover:border-sky-500/30'
                        }`}
                      >
                        <p className="text-sky-300 font-medium">{site.name}</p>
                        <p className="text-xs text-slate-500">{site.url}</p>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => handlePublish(false)}
                    disabled={publishing || !selectedSite}
                    data-testid="btn-submit-publish"
                    className="w-full mt-4 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold py-6"
                  >
                    {publishing ? 'Publication...' : 'Publier'}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ArticlePreview;